import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FILE_SIZES } from 'Common/enums';
import { formatBytes } from '../../GenericListFileUploader/sheetjs-helpers';

export enum POSSIBLE_STATUS {
    loading = 'loading',
    loaded = 'loaded',
    idle = 'idle',
    validFile = 'valid-file',
    validatingHeaders = 'validating-headers',
    processingFile = 'processing-file',
    processingFileComplete = 'processing-file-complete',
    chunking = 'chunking',
    chunkingComplete = 'chunking-complete',
    canceled = 'canceled',
    error = 'error',
    errorLoading = 'error-loading'
}

type Status = POSSIBLE_STATUS;
export type FileDetails = {
    fileName: string;
    fileSize: string;
    totalNumberOfRows: number;
    totalChunks: number;
    addCount: null | number;
    errorRows: any[];
};

export type PCIFileUploaderState = {
    listName: string;
    listType: null | SingleListType;
    listId: number;
    merchantSpecific: boolean;
    maxFileSize: number;
    status: Status;
    fileDetails: FileDetails;
    confirmModalIsOpen: boolean;
    currentChunkNumber: number;
    file: File | null;
    chunkSize: number;
    customListType: undefined | CustomListObj;
};

export const initialState: PCIFileUploaderState = {
    listName: '',
    listType: null,
    listId: -1,
    merchantSpecific: false,
    maxFileSize: FILE_SIZES['100 MB'],
    status: POSSIBLE_STATUS.loading,
    fileDetails: {
        fileName: '',
        fileSize: '',
        totalNumberOfRows: 0,
        totalChunks: 1,
        addCount: null,
        errorRows: []
    },
    file: null,
    confirmModalIsOpen: false,
    currentChunkNumber: 0,
    chunkSize: 1000,
    customListType: undefined
};

const pciFileUploaderSlice = createSlice({
    name: 'pci-file-uploader',
    initialState,
    reducers: {
        updateStatus(state, action: PayloadAction<Status>) {
            state.status = action.payload;
        },
        externalLoad(state, action: PayloadAction<Pick<PCIFileUploaderState, 'maxFileSize' | 'chunkSize'>>) {
            state.status = POSSIBLE_STATUS.loaded;
            state.chunkSize = action.payload.chunkSize;
            state.maxFileSize = action.payload.maxFileSize;
        },
        updateValidFileDetails(state, action: PayloadAction<Partial<FileDetails>>) {
            state.status = POSSIBLE_STATUS.validFile;
            state.fileDetails = {
                ...state.fileDetails,
                ...action.payload
            };
        },
        updateValidFile(state, action: PayloadAction<File>) {
            state.status = POSSIBLE_STATUS.validatingHeaders;
            state.file = action.payload;
            state.fileDetails = {
                ...state.fileDetails,
                fileName: action.payload.name,
                fileSize: formatBytes(action.payload.size)
            };
        },
        updateProcessingCompleteFileDetails(state, action: PayloadAction<Partial<FileDetails>>) {
            state.status = POSSIBLE_STATUS.processingFileComplete;
            state.fileDetails = {
                ...state.fileDetails,
                ...action.payload
            };
        },
        beginChunking(state) {
            state.status = POSSIBLE_STATUS.chunking;
            state.confirmModalIsOpen = false;
        },
        updateCurrentChunkNumber(state, action: PayloadAction<number>) {
            state.currentChunkNumber = action.payload;
        },
        deleteFile(state) {
            state.status = POSSIBLE_STATUS.idle;
            state.file = null;
            state.fileDetails = {
                fileName: '',
                fileSize: '',
                totalNumberOfRows: 0,
                totalChunks: 0,
                addCount: null,
                errorRows: []
            };
            state.confirmModalIsOpen = false;
        },
        toggleConfirmModal(state, action: PayloadAction<{ value: boolean; changeStatus: boolean }>) {
            state.confirmModalIsOpen = action.payload.value;
            // assuming you are cancelling, there is a valid file waiting for you.
            if (action.payload.changeStatus) {
                state.status = POSSIBLE_STATUS.validFile;
            }
        }
    }
});

export const {
    updateStatus,
    updateValidFileDetails,
    updateProcessingCompleteFileDetails,
    deleteFile,
    toggleConfirmModal,
    beginChunking,
    updateCurrentChunkNumber,
    updateValidFile,
    externalLoad
} = pciFileUploaderSlice.actions;
export default pciFileUploaderSlice.reducer;
